import NProgress from 'nprogress';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Suspense, Fragment, useEffect, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
// components
import LoadingScreen from '../components/LoadingScreen';
//
import { PATH_PAGE, PATH_AUTH, PATH_APP_LINK, PATH_INVITE_LINK } from './paths';
import AuthRoutes from './auth.routes';
import DashboardRoutes from './dashboard.routes';
// import RegisterRoutes from './register.routes';
import TrainingRoutes from './training.routes.js';
import lazy from './lazy';
import RegisterLayout from 'src/layouts/register';
import GuestGuard from 'src/guards/GuestGuard';
import * as Sentry from '@sentry/react';

const SentryRoute = Sentry.withSentryRouting(Route);
// ----------------------------------------------------------------------

const nprogressStyle = makeStyles((theme) => ({
  '@global': {
    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        top: 0,
        left: 0,
        height: 2,
        width: '100%',
        position: 'fixed',
        zIndex: theme.zIndex.snackbar,
        backgroundColor: theme.palette.primary.main,
        boxShadow: `0 0 2px ${theme.palette.primary.main}`,
      },
      '& .peg': {
        right: 0,
        opacity: 1,
        width: 100,
        height: '100%',
        display: 'block',
        position: 'absolute',
        transform: 'rotate(3deg) translate(0px, -4px)',
        boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`,
      },
    },
  },
}));

function RouteProgress(props) {
  nprogressStyle();

  NProgress.configure({
    speed: 500,
    showSpinner: false,
  });

  useMemo(() => {
    NProgress.start();
  }, []);

  useEffect(() => {
    NProgress.done();
  }, []);

  return <SentryRoute {...props} />;
}

export function renderRoutes(routes = []) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, idx) => {
          const Component = route.component;
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;

          return (
            <RouteProgress
              key={`routes-${idx}`}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
}

const routes = [
  // Other Routes
  {
    exact: true,
    path: PATH_PAGE.page404,
    component: lazy(() => import('../views/Page404')),
  },
  {
    exact: true,
    path: PATH_PAGE.page500,
    component: lazy(() => import('../views/Page500')),
  },
  {
    exact: true,
    path: PATH_PAGE.unsubscribe,
    component: lazy(() => import('src/views/Unsubscribe')),
  },
  {
    exact: true,
    path: PATH_PAGE.unsubscribeAll,
    component: lazy(() => import('src/views/Unsubscribe/All')),
  },

  {
    exact: true,
    path: PATH_APP_LINK,
    component: lazy(() => import('src/views/AppLink')),
  },
  {
    exact: true,
    path: PATH_INVITE_LINK(':teamName'),
    component: lazy(() =>
      import('src/views/authentication/Register/new/InviteLink')
    ),
    guard: GuestGuard,
  },

  {
    exact: true,
    path: PATH_AUTH.root,
    component: () => <Redirect to={PATH_AUTH.login} />,
  },

  {
    exact: true,
    path: '/',
    component: () => <Redirect to={PATH_AUTH.login} />,
  },
  // {
  //   exact: true,
  //   path: PATH_AUTH.register.joinTeam.email,
  //   component: lazy(() => import('src/views/authentication/Register/Email')),
  //   layout: RegisterLayout,
  // },
  {
    exact: true,
    path: PATH_AUTH.register.joinTeam.email,
    component: lazy(() =>
      import('src/views/authentication/Register/new/JoinTeam')
    ),
  },
  {
    exact: true,
    path: PATH_AUTH.register.joinTeam.invites,
    component: lazy(() => import('src/views/authentication/Register/JoinTeam')),
    layout: RegisterLayout,
  },
  {
    exact: true,
    path: PATH_AUTH.register.joinTeam.info,
    component: lazy(() =>
      import('src/views/authentication/Register/AboutYouJoin')
    ),
    layout: RegisterLayout,
  },
  {
    exact: true,
    path: PATH_AUTH.register.root,
    component: lazy(() =>
      import('src/views/authentication/Register/new/RegisterNew')
    ),
    guard: GuestGuard,
  },
  {
    exact: true,
    path: PATH_AUTH.register.pendingVerification,
    component: lazy(() =>
      import('src/views/authentication/Register/new/PendingVerification')
    ),
  },
  AuthRoutes,
  TrainingRoutes,
  DashboardRoutes,
];

export default routes;
